import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import B2BLayout from '../../../layouts/b2b'
import {
  Space,
  Button,
  Modal,
  Form,
  DatePicker,
  Card,
  Row,
  Col,
  Statistic,
  Skeleton,
  Result,
} from 'antd'
import { getColumns } from './columns'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { useNavigate, useLocation } from 'react-router-dom'
import { Sorter } from '../../../interfaces'
import { objectToUrl } from '../../utils'
import { currentFiltersProps, DataType, Enums, TablePaginationPosition } from './interface'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'
import './index.css'
import FiltersDrawer from '../../../components/v2/filters-drawer'
import {
  getJsonSettingsByKey,
  getColumnOrder,
  TableSettingsButton,
  TransferItem,
} from '../../../components/v2/table-settings-button'
import { HStack } from 'src/react-app/ui/Stack'
import { PrintButton } from '../ui/print-button/print-button'
import { getPrintPath } from '../../../components/v2/export-excel-button'
import DateFilter from './filter-date'
import { getDataOfType } from '../../../../lib/utils/get-data-of-type'
import FixedHeader from '../../../layouts/b2b/fixed-header'
import DataTable from '../../../components/data-table'
import dayjs from 'dayjs'
import { getFilters } from './filters'
import { RouteSheetsCardModal } from './card-modal'
import { RouteSheetsStatistics } from './styles'
import { handleSaveEdit } from './actions'
import { ResultStatusType } from 'antd/lib/result'
import { hasPermission, hasPermissionScope } from 'src/react-app/lib/permissions'
import { Permissions } from 'src/react-app/constants/permissions'

const createMenuCounters = createSelector(
  (state: any) => state.menuV2,
  counters => counters,
)

const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)

function parseUrlSearchParams(search: string): currentFiltersProps {
  const urlParams = new URLSearchParams(search)
  return {
    date: urlParams.get('date') || undefined,
    id: Number(urlParams.get('id')) || undefined,
    vehicle_id: Number(urlParams.get('vehicle_id')) || undefined,
    driver_name: urlParams.get('driver_name') || undefined,
    center_name: urlParams.get('center_name') || undefined,
    zone_groups: urlParams.get('zone_groups') || undefined,
  }
}

const RouteSheetsList = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const { sessionData } = useSelector(createSession)
  const isLeader = getDataOfType(sessionData, 'organization.id', Number, null) === 1
  const isManagerByScope = hasPermissionScope(
    sessionData?.permissionsWithScope,
    Permissions.ACCESS_ORDERS,
    ['office', 'organization', 'brand', 'all'],
  )
  const isManager =
    isManagerByScope ||
    hasPermission(sessionData?.permissionsWithScope, Permissions.ACCESS_LOGISTIC)

  const [data, setData] = useState<DataType[]>([])
  const [totals, setTotals] = useState<Record<string, number>>({})
  const [availableFilters, setAvailableFilters] = useState<Enums>({
    vehicle: [],
    zone_groups: [],
  })
  const [urlParams] = useState(Object.fromEntries(new URLSearchParams(location.search)))
  const [currentFilters, setCurrentFilters] = useState<currentFiltersProps>({})

  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const isFirstLoadCompleteRef = useRef(false)
  const [importLoading, setImportLoading] = useState(false)
  const [bottomCenter] = useState<TablePaginationPosition>('bottomCenter')
  const [sorter, setSorter] = useState<Sorter>({})
  const [pagination, setPagination] = useState({
    current: Number(urlParams?.page) || 1,
    pageSize: 50,
    position: [bottomCenter],
    showSizeChanger: true,
    total: 0,
  })

  const { counters } = useSelector(createMenuCounters)
  const [localCounter, setLocalCounter] = useState<null | number>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [enums, setEnums] = useState<any>({})
  const [fieldTypes, setFieldTypes] = useState<any>({})
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [selectedRouteSheet, setSelectedRouteSheet] = useState<DataType | null>(null)

  const [isModalImportVisible, setIsModalImportVisible] = useState(false)
  const [importForm] = Form.useForm()

  const headerRef = useRef<HTMLDivElement | null>(null)

  interface IError {
    code?: ResultStatusType
    message?: string
  }
  const [error, setError] = useState<null | IError>(null)

  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, location.pathname))

  useEffect(() => {
    const newFilters = parseUrlSearchParams(location.search)
    setCurrentFilters(newFilters)
  }, [location.search])

  useEffect(() => {
    const newCounterData = counters?.['/route-sheets']
    if (newCounterData && !localCounter) {
      setLocalCounter(newCounterData)
    }
    if (newCounterData && localCounter && newCounterData !== localCounter) {
      setLocalCounter(newCounterData)
      getData()
    }
  }, [counters])

  const breadcrumbs = useMemo(() => {
    return [
      {
        href: '/route-sheets',
        title: 'Маршрутные листы',
      },
    ]
  }, [])

  const getAvailableFilters = useCallback(() => {
    fetchAPI(`/api/logistics/route-sheets/enum-list`)
      .then(data => {
        setEnums(data)

        const filters = {
          vehicle: data?.vehicle_id?.map((i: any) => ({ label: i.value, value: i.id })) || [],
          zone_groups: data?.zone_groups?.map((i: any) => ({ label: i.value, value: i.id })) || [],
        }

        setAvailableFilters(filters)
      })
      .catch(() => {
        console.error('Не удалось получить список доступных фильтров')
      })
  }, [])

  const getFieldTypes = useCallback(() => {
    fetchAPI(`/api/logistics/route-sheets/field-types`)
      .then(data => {
        setFieldTypes(data)
      })
      .catch(() => {
        console.error('Не удалось получить список доступных фильтров')
      })
  }, [])

  const getData = useCallback(() => {
    try {
      if (!isFirstLoadCompleteRef.current) {
        setLoading(true)
      } else {
        setTableLoading(true)
      }
      const urlParamsObj = Object.fromEntries(new URLSearchParams(location.search))

      fetchAPI(`/api/logistics/route-sheets/list${objectToUrl(urlParamsObj)}`)
        .then(({ data, pagination, sorter, totals }) => {
          if (data) {
            setData(
              data.map((item: any) => ({
                ...item,
              })),
            )

            pagination.hide = !!selectedDate

            setPagination(prevState => ({
              ...prevState,
              ...pagination,
            }))
            setSorter(sorter)

            if (totals) {
              console.log('totals', totals)
              setTotals(totals)
            }
          }
          if (!isFirstLoadCompleteRef.current) {
            isFirstLoadCompleteRef.current = true
          }
        })
        .catch(e => {
          setError({
            message: (e as IHttpError)?.message || 'Неизвестная ошибка',
            code: ((e as IHttpError)?.statusCode || 'error') as ResultStatusType,
          })
        })
        .finally(() => {
          setLoading(false)
          setTableLoading(false)
        })
    } catch (error) {
      console.error('Ошибка при получении данных:', error)
    }
  }, [location.search])

  useEffect(() => {
    getAvailableFilters()
    getFieldTypes()
  }, [])

  useEffect(() => {
    getData()
  }, [location.search])

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    const currentUrlParams = Object.fromEntries(new URLSearchParams(location.search))
    setPagination(prevState => ({ ...prevState, ...pagination }))

    const current = {
      ...currentUrlParams,
      sort_order: sorter?.order,
      sort_field: sorter?.field,
    } as any

    if (pagination.current) {
      current.page = pagination.current
    }
    if (pagination.pageSize) {
      current.pageSize = pagination.pageSize
    }
    if (current?.page === 1) {
      delete current.page
    }
    if (current?.sort_order === undefined) {
      delete current.sort_order
      delete current.sort_field
    }

    const urlParams = new URLSearchParams(current).toString()
    navigate({ search: urlParams })
  }

  const handleRowClick = (record: DataType) => {
    setSelectedRouteSheet(record)
  }

  const handleModalClose = useCallback(() => {
    setSelectedRouteSheet(null)
  }, [])

  const onSelectChange = (selectedKeys: React.Key[]) => {
    setSelectedRowKeys(selectedKeys)
  }

  const changeFiltersHandler = useCallback(
    (filters: any) => {
      let tmpFilter = { ...(filters || {}), pageSize: pagination.pageSize || 50 }
      const newUrlParams = new URLSearchParams(tmpFilter).toString()
      navigate({ search: newUrlParams })
      //getData()
    },
    [pagination.pageSize, getData, history],
  )

  const columns = useMemo(() => getColumns(isLeader || isManager, sorter), [
    isLeader,
    isManager,
    sorter,
  ])

  const viewColumns = useMemo(() => {
    const defaultInput = { type: 'string' }

    const enhanceColumn = (column: any) => {
      if (!('dataIndex' in column) || !column.editable) {
        return column
      }

      const dataIndex = column.dataIndex

      const fieldType = fieldTypes[dataIndex]
      let input = fieldType || defaultInput

      return {
        ...column,
        input: {
          ...input,
          key: dataIndex,
        },
      }
    }

    const columnOrder = getColumnOrder(jsonSettings)

    if (columnOrder.length > 0) {
      const enhancedColumns = columns.map(enhanceColumn)

      let result: any[] = []
      for (let i = 0; i < columnOrder.length; i++) {
        const item = enhancedColumns.find(column => columnOrder[i] === column.dataIndex)
        if (item) {
          result.push(item)
        }
      }
      return result
    }

    return columns.map(enhanceColumn)
  }, [columns, jsonSettings, enums])

  const editableFields = useMemo(() => {
    return viewColumns.filter(column => column.editable).map(column => column.dataIndex as string)
  }, [viewColumns])

  const excelPath = useMemo(() => {
    return getPrintPath('/api/logistics/route-sheets/list', new URLSearchParams(location.search))
  }, [location.search])

  const settingsColumns = useMemo(() => {
    return columns.map(column => ({
      key: 'dataIndex' in column ? column.dataIndex : undefined,
      title: column.title,
    }))
  }, [columns]) as TransferItem[]

  // Определять выбрана ли одна дата доставки в фильтре
  useEffect(() => {
    if (currentFilters?.date) {
      const [startDate, endDate] = currentFilters.date.split('-')

      if (startDate === endDate) {
        setSelectedDate(startDate)
      } else {
        setSelectedDate('')
      }
    }
  }, [currentFilters])

  // Событие изменение фильтров
  const changeDateFilterHandler = (newDate: string) => {
    setLoading(true)
    setData([])
    const urlParams = new URLSearchParams(location.search)

    if (newDate) {
      urlParams.set('date', `${newDate}-${newDate}`)
    } else {
      urlParams.delete('date')
    }

    navigate({ search: urlParams.toString() })
    setSelectedDate(newDate)
  }

  // Событие пагинации
  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prevState => ({
      ...prevState,
      current: Number(page) || 1,
      pageSize: pageSize,
    }))

    const currentUrlParams = new URLSearchParams(location.search)
    currentUrlParams.set('page', page.toString())
    currentUrlParams.set('pageSize', pageSize.toString())
    navigate({ search: currentUrlParams.toString() })
  }

  const handleImportData = useCallback(
    async (values?: { date: any }) => {
      setImportLoading(true)

      const date = values ? dayjs(values.date).format('DD.MM.YYYY') : dayjs().format('DD.MM.YYYY')

      try {
        const [ryazanResponse, kalugaResponse] = await Promise.all([
          fetchAPI(`/api/logistics/route-sheets/import?date=${date}&center_name=Рязань`),
          fetchAPI(`/api/logistics/route-sheets/import?date=${date}&center_name=Калуга`),
        ])

        const allFailedOrders = [
          ...(ryazanResponse.failed_orders || []),
          ...(kalugaResponse.failed_orders || []),
        ]

        if (allFailedOrders.length > 0) {
          const errorMessages = allFailedOrders.map(order => (
            <div key={order.id}>
              <b>{`Заказ ${order.id}`}</b>
              {`: ${order.errors.join(', ')}`}
            </div>
          ))

          Modal.error({
            title: 'Ошибки при выгрузке',
            content: <div>{errorMessages}</div>,
          })
        }

        getData()
        setIsModalImportVisible(false)
        importForm.resetFields()
      } catch (error) {
        console.error('Ошибка при импорте данных:', error)
      } finally {
        setImportLoading(false)
      }
    },
    [getData, importForm],
  )

  const showImportModal = useCallback(() => {
    setIsModalImportVisible(true)
  }, [])

  const handleImportModalCancel = useCallback(() => {
    setIsModalImportVisible(false)
    importForm.resetFields()
  }, [importForm])

  const formatNumberWithSpace = (value: number | string): string => {
    return Number(value).toLocaleString('ru-RU', {
      maximumFractionDigits: 0,
    })
  }

  const onSaveEdit = handleSaveEdit({
    setLoading,
    getData,
    setSelectedRowKeys,
  })

  return (
    <B2BLayout breadcrumbs={breadcrumbs} isViewScrollButton>
      {loading && <Skeleton active />}
      {!loading && error && <Result status={error.code} title="Ошибка" subTitle={error.message} />}
      {!loading && !error && (
        <>
          <FixedHeader ref={headerRef}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }} align={'start'} wrap>
              <Space>
                <DateFilter selectedDate={selectedDate} onChange={changeDateFilterHandler} />
                <Button type="primary" onClick={showImportModal} loading={importLoading}>
                  Обновить данные
                </Button>
              </Space>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }} align={'start'}>
                <HStack gap={'8'} align={'start'}>
                  <FiltersDrawer
                    filters={getFilters({ currentFilters, availableFilters })}
                    onChange={changeFiltersHandler}
                    currentFilters={currentFilters}
                  />
                  <PrintButton path={excelPath} columns={viewColumns} />
                  <TableSettingsButton items={settingsColumns} />
                </HStack>
              </Space>
            </Space>
          </FixedHeader>

          <RouteSheetsStatistics>
            <Card style={{ marginBottom: '20px' }}>
              <Statistic
                title="Стоим. доставки"
                value={totals.total_delivery_amount || 0}
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    formatNumberWithSpace(value)
                  )
                }
                suffix={loading ? null : '₽'}
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Стоим. перевозчика"
                value={totals.carrier_cost || 0}
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    formatNumberWithSpace(value)
                  )
                }
                suffix={loading ? null : '₽'}
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Маржа"
                value={(totals.total_delivery_amount || 0) - (totals.carrier_cost || 0)}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    formatNumberWithSpace(value)
                  )
                }
                suffix={loading ? null : '₽'}
                valueStyle={{
                  color:
                    (totals.total_delivery_amount || 0) - (totals.carrier_cost || 0) < 0
                      ? '#ff0000'
                      : '#1890ff',
                }}
              />
              <Statistic
                title="Эффективность"
                value={totals.efficiency || 0}
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    totals.efficiency
                  )
                }
                valueStyle={{
                  color: (totals.efficiency || 0) < 1 ? '#ff0000' : '#1890ff',
                }}
              />
              <Statistic
                title="Километраж"
                value={totals.distance || 0}
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    formatNumberWithSpace(value)
                  )
                }
                suffix={loading ? null : 'км'}
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Кол-во изделий"
                value={totals.total_items_count || 0}
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    `${formatNumberWithSpace(value)}`
                  )
                }
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Стоим. доставки за изделие"
                value={
                  totals.total_items_count
                    ? (totals.total_delivery_amount || 0) / (totals.total_items_count || 0)
                    : 0
                }
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    `${formatNumberWithSpace(value)}`
                  )
                }
                suffix={loading ? null : '₽'}
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Стоим. перевозчика за изделие"
                value={
                  totals.total_items_count
                    ? (totals.carrier_cost || 0) / totals.total_items_count
                    : 0
                }
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    `${formatNumberWithSpace(value)}`
                  )
                }
                suffix={loading ? null : '₽'}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </RouteSheetsStatistics>
          <DataTable
            columns={viewColumns}
            data={data}
            loading={tableLoading}
            rowKey={r => String(r.id)}
            pagination={pagination}
            selectedRowKeys={selectedRowKeys}
            onSelectChange={onSelectChange}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            onTableChange={handleTableChange}
            className={'route-sheets-table'}
            headerRef={headerRef}
            editableFields={editableFields}
            onSaveEdit={onSaveEdit}
            maxLines={2}
            totals={totals}
            bordered
            highlightLastRowPrefix={'route-sheet'}
          />
          <Modal
            title="Выбор даты для обновления"
            open={isModalImportVisible}
            onCancel={handleImportModalCancel}
            okButtonProps={{ loading: importLoading }}
            onOk={() => importForm.submit()}
          >
            <Form form={importForm} onFinish={handleImportData} layout="vertical">
              <Form.Item
                name="date"
                label="Дата поездки"
                rules={[{ required: true, message: 'Укажите дату поездки' }]}
              >
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Form>
          </Modal>
          <RouteSheetsCardModal routeSheet={selectedRouteSheet} onClose={handleModalClose} />
        </>
      )}
    </B2BLayout>
  )
}

export default RouteSheetsList
